import { TableName } from "../../../tables"
import { Currency } from "../../../tables/types/types"
import { PaymentCategory, VatCondition, VatTaxCode } from "../../administration"
import { Company, IdentifierType, SalePoint } from "../../system"
import { Address } from "../../../features/address/types"

export type Perception = {
    name: string
    baseAmount: number
    yield: number
    amount: number
    legalCode: number
}

export enum FiscalDocumentType {
    INVOICE = 'Invoice',
    CREDIT_NOTE = 'CreditNote',
    DEBIT_NOTE = 'DebitNote'
}

export enum FiscalDocumentClass {
    A = 'A',
    B = 'B',
    C = 'C',
    E = 'E'
}

export type CustomerData = {
    identifierType: IdentifierType,
    identifier: number,
    name: string,
    vatCondition: VatCondition,
    emails?: string[]
    address?: Address
}

export type FiscalDocumentItem = {
    name: string
    unitTypeName: string
    vatTaxCode?: VatTaxCode
    vatTaxYield: number
    unitSubtotal: number
    unitTotal: number
    quantity: number
    totalWithoutVAT: number
    vatAmount: number
}

// Descuento o recargo a nivel comprobante. El monto es bruto (con IVA y con el recargo del
// medio de pago, sin percepciones) y el emisor lo prorratea entre los ítems por participación.
export type Discount = {
    reason: string
    amount: number
    kind?: string
}

export type FiscalDocumentDetails = {
    fiscalData: {
        documentType: FiscalDocumentType
        documentClass: FiscalDocumentClass
        companyTaxId: number
        saleProvince: string
        salePoint: SalePoint
        fiscalDocumentParentIds?: string[]
    }
    company: Company
    customer: CustomerData
    // Condiciones de venta que imprime el comprobante. El crédito a favor llega sin paymentTypeId.
    paymentItems?: {
        paymentTypeId?: string
        paymentCategory: PaymentCategory
        amount: number
        currency: Currency
        installments?: number
    }[]
    items: FiscalDocumentItem[]
    discounts?: Discount[]
    perceptions: Perception[]
    paymentTypeYield?: number
    totals: {
        totalWithoutVAT: number
        vatAmount: number
        totalPerceptions: number
        total: number
    }
    creationDate: string
}

export enum FiscalDocumentStatus {
    AUTHORIZED = 'Authorized',
    PARTIALLY_INVOICED = 'PartiallyInvoiced',
    PENDING = 'Pending',
    PREVIEW = 'Preview',
    REJECTED = 'Rejected',
    TO_AUTHORIZE = 'ToAuthorize',
    NOT_APPLICABLE = 'NotApplicable'
}

// Imputación guardada al emitir una factura de acopio: permite revertir cantidades al anular.
export type HoardInvoiceAllocation = {
    mode: string
    itemQuantities: {
        itemId: string
        quantity: number
    }[]
    fullInvoice?: boolean
}

export type FiscalDocument = {
    companyId: string
    id: string
    externalId?: number
    status: FiscalDocumentStatus
    table: TableName
    operationId: string
    documentType: FiscalDocumentType
    documentClass: FiscalDocumentClass
    associatedFiscalDocumentId?: string
    adjustmentNoteId?: string
    fiscalDocumentParentIds?: string[]
    code?: string
    statusMessage?: string
    details?: FiscalDocumentDetails
    issueDate: string
    pdfUrl?: string
    hoardInvoiceAllocation?: HoardInvoiceAllocation
    // En la factura anulada: la factura sigue AUTHORIZED; "Anulada" es un badge derivado.
    annulledAt?: string
    annulledByUserId?: string
    annulmentCreditNoteId?: string
    // Marca previa a crear la NC de anulación; se limpia al autorizar o rechazar.
    annulmentInFlight?: boolean
    annulmentInFlightByUserId?: string
    annulmentInFlightAt?: string
    // En la NC de anulación: la distingue de una NC manual.
    annulmentOfFiscalDocumentId?: string
    annulmentRequestedByUserId?: string
}
